
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphql } from '@client/shared/graphql/graphql.ts';
import { type ResultOf } from 'gql.tada';
import { gqlRequest } from '@client/shared/graphql/client.ts';

export type Agent = ResultOf<typeof AgentsQuery>['agents'][number];

export const AgentsQuery = graphql(`
  query Agents {
    agents {
      id
      name
      bookable
    }
  }
`);

export const AGENTS_QUERY_KEY = ['agents', 'group'] as const;

export async function fetchAgents(signal?: AbortSignal): Promise<Agent[]> {
  const data = await gqlRequest(AgentsQuery, {}, { signal });
  return data.agents;
}

export function agentNameMap(agents: readonly Agent[]): ReadonlyMap<string, string> {
  return new Map(agents.map((a) => [a.id, a.name]));
}

export function useAgents(): readonly Agent[] {
  const agentsQuery = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: ({ signal }) => fetchAgents(signal),
  });
  return useMemo(
    () => [...(agentsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [agentsQuery.data],
  );
}

export function useAgentNameMap(): ReadonlyMap<string, string> {
  const agentsQuery = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: ({ signal }) => fetchAgents(signal),
  });
  return useMemo(() => agentNameMap(agentsQuery.data ?? []), [agentsQuery.data]);
}
